import Util from '../util';
import ProviderGoogle from './provider-google';
import FileMeta from './file-meta';
import Translate from '../translate';
import * as log from 'loglevel';
import {debug} from 'loglevel';
import {ThemeConfig} from '../const';
import {dimensionExportSize, sameExportSize} from './export-size';

class Upload {

    constructor(options) {
        // Default settings
        let defaults = {
            api: '',
            queryParams: {},
            errorPrefix: 'upload.'
        };

        // Merge options into defaults
        this.settings = Util.extend(defaults, options || {});

        this._fileMeta = new FileMeta({ api: this.settings.api, queryParams: this.settings.queryParams, origin: 4 }); // origin = 4 (options app)
        this._provider = new ProviderGoogle({ api: this.settings.api, queryParams: this.settings.queryParams });
    }

    static getInstance(options){
        if(Upload._instance){
            return Upload._instance;
        }

        // store instance on class as static property
        Upload._instance = new Upload(options); // options only required on first call

        return Upload._instance;
    }

    controller(option, product, productForm, getOption){
        // stateful controller object for the upload option
        let controller = {
            // scoped to the controller
            files: [],
            inputs: [],
            onValidate: (option) => {

                // NEW: send crop apply event (just in case crop mode is still active)
                productForm.dispatchEvent(new CustomEvent('option:crop:apply', { detail: option, cancelable: true, bubbles: true }));

                let valid = true;
                // required validation based on local state
                if(option.required && controller.files.length === 0){
                    option.errors.push(Translate.get(this.settings.errorPrefix + 'required'));

                    valid = false;
                }

                return valid;
            },
            onChange: (e,option, productForm, productFormLink, onChange) => {

                let target = e.target;

                if(target.value && target.files.length > 0){

                    const files = target.files;

                    let fileMetaArray = [];

                    for (let i = 0; i < files.length; i++) {

                        const file = files[i];

                        // 10MB max default
                        let maxMb = option.params.maxMb || 10;
                        const maxAllowedSize = maxMb * 1024 * 1024;
                        if (file.size > maxAllowedSize) {
                            target.value = '';

                            option.errors = option.errors || [];
                            option.errors.push(Translate.get(this.settings.errorPrefix + 'max', { max: maxMb }));

                            // force new render of options to show the error
                            target.dispatchEvent(new CustomEvent('options:render', { detail: option, cancelable: true, bubbles: true }));
                            return;
                        }

                        fileMetaArray.push(this._fileMeta.fromNative(file));
                    }


                    // upload option element
                    let wrapper = Util.findParent(target, '.cl-po--upload');
                    let progressEl = wrapper.querySelector('progress');
                    progressEl.max = 100;
                    progressEl.value = 0;
                    progressEl.style.display = 'block';

                    this.upload(fileMetaArray, (progress) => {

                        if (progress.lengthComputable) {
                            let value = ((progress.loaded / progress.total) * 100);
                            // we set fake progress on preview export ( avoid setting below )
                            if (value > progressEl.value) {
                                progressEl.value = value;
                            }
                        }

                    }).then((files) => {

                        progressEl.style.display = 'none';

                        // remove all current files ( request fire & forget - not really important to remove on the server )
                        for (let i = 0; i < controller.files.length; i++) {
                            const file = controller.files[i];
                            if(file.uuid){
                                this._fileMeta.removeFile(file);
                            }
                        }

                        // remove all inputs ( we rebuild all of them - makes multiple files simpler )
                        for (let i = 0; i < controller.inputs.length; i++) {
                            const input = controller.inputs[i];
                            if(input.parentNode){
                                input.parentNode.removeChild(input);
                            }
                        }
                        // reset inputs array
                        controller.inputs.length = 0;

                        // store new uploaded files
                        controller.files = files;

                        /* INFO: UI with 'x' to remove files individually we could support append in multiple mode BUT we leave this for Upload-Lift
                        if(option.params.multiple){
                            // append all
                            controller.files.push(...files);
                        } else {
                            // -> remove old files
                            // overwrite
                            controller.files = files;
                        } */

                        let value = '';
                        for (let i = 0; i < controller.files.length; i++) {
                            const file = controller.files[i];

                            // cors url in option value
                            let url = this._corsUrl(file.url);

                            value += url;
                            let num = i + 1;
                            if(num < controller.files.length){
                                value += ',';
                            }

                            // add hidden input
                            let inputEl = document.createElement('input');
                            inputEl.type = 'hidden';
                            // multiple number suffix
                            if(option.params.multiple){
                                inputEl.name = 'properties[' + option.name + '_' + num + ']';
                            } else {
                                inputEl.name = 'properties[' + option.name + ']';
                            }

                            // we store domain url on properties same as with upload-lift
                            inputEl.value = file.url;

                            // NEW: link option to form (required when form is not parent)
                            if(productFormLink){
                                inputEl.setAttribute('form', productFormLink);
                            }

                            // add to content
                            wrapper.appendChild(inputEl);
                            // save reference to remove on updates
                            controller.inputs.push(inputEl);
                        }

                        option.value = value;

                        // IMPORTANT: reset file upload selection ( to avoid upload again on add to cart )
                        target.value = '';

                        onChange(option);

                        // NEW: update dimension option based on uploaded file
                        if(option.params.optionDimension) {
                            // IMPORTANT:
                            // we update the dimension after the upload field change event (required to evaluate conditions to show the dimension option)
                            // async/await in getImgSize makes sure that the dimension option is already rendered
                            this._dimensionUpdate(option, getOption(option.params.optionDimension), value); // value already CORS
                        }

                    }).catch((error) => {

                        // NEW: file upload failed - show basic alert
                        alert(error.error || Translate.get(this.settings.errorPrefix + 'required'));

                        progressEl.style.display = 'none';

                        // IMPORTANT: reset file upload selection ( to avoid upload again on add to cart )
                        target.value = '';
                    });
                }

            },
            update: (option, productForm, productFormLink) => {
                // outside value update (cart restore, saved configuration)
                if(option.value){

                    if(controller.inputs.length === 0){

                        // NEW: multiple files arrive comma separated - a single field keeps the first one only
                        let urls = option.value.split(',').filter((url) => url);
                        if(!option.params.multiple){
                            urls.length = 1;
                        }

                        let inputEls = [];

                        for (let i = 0; i < urls.length; i++) {
                            const url = urls[i];

                            // add file to pass validation
                            controller.files.push({
                                url: url,
                                name: 'file' + url.substring(url.lastIndexOf('.')), // we don't have the original name anymore
                                meta: {}
                            });

                            // add hidden input
                            let inputEl = document.createElement('input');
                            inputEl.type = 'hidden';
                            // multiple number suffix ( same as after an upload )
                            if(option.params.multiple){
                                inputEl.name = 'properties[' + option.name + '_' + (i + 1) + ']';
                            } else {
                                inputEl.name = 'properties[' + option.name + ']';
                            }

                            // we store domain url on properties same as with upload-lift
                            inputEl.value = url;

                            // NEW: link option to form (required when form is not parent)
                            if(productFormLink){
                                inputEl.setAttribute('form', productFormLink);
                            }

                            inputEls.push(inputEl);
                            // save reference to remove on updates
                            controller.inputs.push(inputEl);
                        }

                        // update the option value back to the cors url to work with live preview
                        option.value = urls.map((url) => this._corsUrl(url)).join(',');

                        // INFO: this needs to be better solved when we have native lit controllers
                        productForm.addEventListener('options:rendered', () => {
                            // add to content
                            let wrapper = document.querySelector('[data-option="' + option.name + '"] .cl-po--upload');
                            if(wrapper){
                                inputEls.forEach((inputEl) => wrapper.appendChild(inputEl));
                            }
                        }, { once: true });
                    }

                }
                // outside reset
                else if(controller.inputs.length > 0){
                    // remove all inputs
                    for (let i = 0; i < controller.inputs.length; i++) {
                        const input = controller.inputs[i];
                        if(input.parentNode){
                            input.parentNode.removeChild(input);
                        }
                    }
                    // reset inputs array
                    controller.inputs.length = 0;
                    controller.files.length = 0;
                }
            },
            remove: (option, productForm, file, onChange) => {

                // remove the file
                for (let i = 0; i < controller.files.length; i++) {
                    // find the file
                    if(controller.files[i] === file){
                        // remove on server
                        this._fileMeta.removeFile(file);
                        // remove from array
                        controller.files.splice(i, 1);

                        for (let j = 0; j < controller.inputs.length; j++) {
                            if (controller.inputs[j].value == file.url) {
                                let input = controller.inputs[j];
                                if(input.parentNode){
                                    input.parentNode.removeChild(input);
                                }
                                controller.inputs.splice(j, 1)
                            }
                        }

                        // INFO: to support multiple files would require rebuild of values
                        option.value = '';

                        onChange(option);

                        break;
                    }
                }
            },
            crop: (option, productForm) => {
                productForm.dispatchEvent(new CustomEvent('option:crop', { detail: option, cancelable: true, bubbles: true }));
            },
        }

        this._migrateDimensionParams(option);

        return controller;
    }

    controllerLift(option, product, productForm, onChange, getOption){

        // stateful controller object for the upload option
        let controller = {
            option: option, // reference which is updated in template render to support full options updates in editorMode
            selector: `.cl-po--upload-lift-${option.id}`,
            container: null,
            field: null, // UploadField instance ref
            urls: [],
            replace: async (layer) => {
                // Match UploadRenderer: fileIndex is one based, absent means the latest file.
                const index = (layer.params.fileIndex || controller.urls.length) - 1;
                const url = controller.urls[index];
                const input = controller.container?.querySelector('input[type=file]');
                if (!url) {
                    // empty slot > normal add flow
                    input?.click();
                    return null;
                }
                const file = await this._pickFile(input?.accept || controller.option.params.accept || 'image/*');
                if (!file) return null;
                // Only LPO pauses value updates; Upload-Lift keeps its normal input events (and rejects while another upload runs).
                controller.replacing = true;
                try {
                    const replacement = await controller.field.replaceFile(url, file);
                    const variant = controller.option.params.variant || 'original';
                    // the configured variant can be missing > keep the uploaded file as fallback
                    const image = [replacement, ...Object.values(replacement.childs || {})].find(item => item.meta?.variant === variant) || replacement;
                    // the old url can be gone (removed while the picker was open) > append instead
                    const current = controller.urls.indexOf(url);
                    if(current === -1) controller.urls.push(image.url); else controller.urls[current] = image.url;
                    controller.syncValue();
                    if (controller.option.params.optionDimension) {
                        this._dimensionUpdate(controller.option, getOption(controller.option.params.optionDimension), this._corsUrl(image.url));
                    }
                    return replacement;
                } finally { controller.replacing = false; }
            },
            onValidate: (option) => {
                // Replacement includes removing the old file after FilePond finishes uploading.
                if (controller.replacing) {
                    option.errors.push('The upload is busy. Please wait until it finishes.');
                    return false;
                }

                // dimension crop update pending > force crop first (and skip add to cart)
                if(option._controller._cropConfig){
                    controller.updateCrop();
                    return false;
                }

                let valid = true;
                // required validation based on local state
                if(option.required && controller.urls.length === 0){
                    option.errors.push(Translate.get(this.settings.errorPrefix + 'required'));
                    valid = false;
                } else if(controller.field) {
                    // trigger full field validation
                    return controller.field.validate();
                }
                return valid;
            },
            syncValue: () => {

                let value = '';
                for (let i = 0; i < controller.urls.length; i++) {
                    let url = this._corsUrl(controller.urls[i]);

                    value += url;
                    let num = i + 1;
                    if (num < controller.urls.length) {
                        value += ',';
                    }
                }

                controller.option.value = value;

                // trigger value changed
                onChange(controller.option);
            },
            _removeByUrl: (url) => { // only used by group-editor

                // HACKY: remove file by url (already CORSed)
                const fileName = url.substring(url.lastIndexOf('/') + 1);

                let fileUuids = Object.keys(controller.field._upload.files);
                for(const uuid of fileUuids){
                    const file = controller.field._upload.files[uuid];
                    if(file.path.includes(fileName)){
                        let files = controller.field._upload._pond.getFiles();
                        for (const fileObj of files) {
                            if(file.uuid === fileObj.serverId){
                                controller.field._upload._pond.removeFile(fileObj, { revert: true }); // IMPORTANT: to remove from internal upload-lift field storage
                            }
                        }
                        break;
                    }
                }
            },
            watchUploads: () => {
                controller.container = document.querySelector(controller.selector);

                if(controller.container){


                    controller.container.addEventListener('upload:added', async (e) => {
                        if (controller.replacing) return;
                        /* event detail contains the file reference */
                        var file = e.detail;

                        // access own option params via controller (editorMode messes up reference of option)
                        const params = controller.option.params;

                        // INFO: currently no support in backend (original looks way better on preview - thumbnail needs at least 1000px)
                        let variant = params.variant || 'original';

                        if(file.url && file.meta.variant === variant){
                            controller.urls.push(file.url);

                            controller.syncValue();

                            // NEW: update dimension option based on uploaded file
                            if(params.optionDimension) {
                                // IMPORTANT:
                                // we update the dimension after the upload field change event (required to evaluate conditions to show the dimension option)
                                // async/await in getImgSize makes sure that the dimension option is already rendered
                                this._dimensionUpdate(controller.option, getOption(params.optionDimension), this._corsUrl(file.url));
                            }
                        }
                    });

                    controller.container.addEventListener('upload:removed', (e) => {
                        /* event detail contains the file reference */
                        var file = e.detail;

                        let variant = controller.option.params.variant || 'original';

                        if(!controller.replacing && file?.url && file.meta.variant === variant){

                            let index = controller.urls.indexOf(file.url);
                            if(index !== -1){
                                // remove
                                controller.urls.splice(index, 1);

                                controller.syncValue();
                            }
                        }

                        // NEW: re-enable cropEdit once the restored file is gone (user replacing it)
                        const params = controller.option.params;
                        if(params._cropEdit){
                            params.cropEdit = params._cropEdit;
                            delete params._cropEdit;
                        }
                    });
                }

            },
            watchCrop: (productForm, getOption) => {

                // editorMode: de-register previous event handler
                if(option._controller.optionChanged){
                    productForm.removeEventListener('option:changed', option._controller.optionChanged);
                }

                const initCrop = () => {
                    // IMPORTANT: read the initial crop ratio from referenced option
                    // INFO: this code is time sensitive, we execute this after the upload "ready" event, this assumes that the format option is always initialized faster (not prefect)
                    let optionDimension = getOption(option.params.optionDimension);

                    if(optionDimension){
                        log.debug('init crop dimension based on option', optionDimension);

                        checkCrop(optionDimension, true);

                        // watch for changes on crop option
                        option._controller.optionChanged = (e) => {
                            // check if referenced option changed
                            if(e.detail.name === option.params.optionDimension){
                                log.debug('updating crop dimension based on option', optionDimension);
                                checkCrop(e.detail);
                            }
                        };

                        // listen to all option changes
                        productForm.addEventListener('option:changed', option._controller.optionChanged);

                        // as we don't directly update the crop config (it would open the editor) we watch if a file is removed - then trigger the crop update (to have the right config for the next upload)
                        // dimension upload should only be used with single file upload - multi upload can only auto open the most recent file
                        controller.container.addEventListener('upload:removed', (e) => {
                            if(option._controller._cropConfig){
                                option._controller.updateCrop();
                            }
                        });
                    }
                }

                const checkCrop = (productOptionCrop, updateField = false) => {

                    // check if dimension controller has x/y set                                                           && no validation errors
                    if(productOptionCrop._controller && productOptionCrop._controller.x && productOptionCrop._controller.y && (!productOptionCrop.errors || productOptionCrop.errors.length === 0)) {

                        // default always update config by setting a invalid crop ratio
                        if(typeof option._controller._cropRatio === 'undefined'){
                            option._controller._cropRatio = -1;
                        }

                        // compare ratio with two decimals
                        let ratioUpload = option._controller._cropRatio;
                        let ratioOption;

                        // ratio is inverse (y/x) on older image editor
                        if(option._controller.field.config.imageEditorPintura){     // UPDATE: rounding to 4 decimals (required for exact uploads input pixels to match output)
                            ratioOption = Math.round((productOptionCrop._controller.x / productOptionCrop._controller.y) * 10000) / 10000;
                        } else {
                            ratioOption = Math.round((productOptionCrop._controller.y / productOptionCrop._controller.x) * 10000) / 10000;
                        }

                        if (ratioUpload !== ratioOption) {

                            // store current ratio to only apply on crop (allows to change dimensions back/forth to avoid crop if ratio matches again)
                            option._controller._cropRatioCurrent = ratioOption;

                            // only update parts of the config
                            let config = {
                                "imageSizeValidate": true,  // REQUIRED: to re-check the crop ratios
                                "imageEditorCropRestrict": true,
                                "imageEditorCropRatios": [{"label": "Dimension", "value": ratioOption}]
                            };

                            log.debug('updating upload-lift crop dimension', config);

                            // store config for later update (shows crop button below upload button)
                            option._controller._cropConfig = config;

                            // direct update on init only OR if no file is uploaded yet
                            if(updateField || !option.value){
                                option._controller.updateCrop();
                            }
                        } else {
                            // ratio matches again clear crop config
                            delete option._controller._cropConfig;
                        }
                    }

                };

                // IMPORTANT: we need to wait for the upload (filepond) element to be ready (not just the field)
                if(controller.field._upload){
                    initCrop();
                } else {
                    controller.field.element.addEventListener('upload:ready', initCrop);
                }

            },
            // NEW: the order's physical size for upload exports (served export entries with `size: true`) from the linked
            // dimension option in this option's dimensionUnit - sent on load and after every render (a value change or the
            // dimension hidden by its conditions), null (config default size) when it has none
            watchExportSize: (productForm, getOption) => {

                // editorMode: de-register previous event handler
                if(option._controller.exportSizeChanged){
                    productForm.removeEventListener('options:rendered', option._controller.exportSizeChanged);
                }

                const sendSize = (optionDimension) => {
                    const size = dimensionExportSize(optionDimension, controller.option.params.dimensionUnit);
                    // IMPORTANT: only on changes - updateConfig({exportSize}) rebuilds the export inputs
                    if(option._controller._exportSizeSent && sameExportSize(option._controller._exportSize, size)){
                        return;
                    }
                    option._controller._exportSize = size;
                    option._controller._exportSizeSent = true;
                    log.debug('updating upload-lift export size', size);
                    option._controller.field.updateConfig({ exportSize: size });
                };

                const initSize = () => {
                    sendSize(getOption(controller.option.params.optionDimension));

                    // after the render: the conditions (visibility) are evaluated then - option:changed fires before them
                    option._controller.exportSizeChanged = () => sendSize(getOption(controller.option.params.optionDimension));
                    productForm.addEventListener('options:rendered', option._controller.exportSizeChanged);
                };

                // IMPORTANT: updateConfig needs the upload (filepond) element to be ready (not just the field)
                if(controller.field._upload){
                    initSize();
                } else {
                    controller.field.element.addEventListener('upload:ready', initSize, { once: true });
                }
            },
            updateCrop: () => {
                // set current crop ratio as active (to compare again on next change)
                option._controller._cropRatio = option._controller._cropRatioCurrent;
                //  update field config > opens crop mode if ratio does not match
                option._controller.field.updateConfig(option._controller._cropConfig);
                // reset to avoid validation and hide crop button
                delete option._controller._cropConfig;
            },
            update: (option, productForm) => {
                // outside value update (cart restore)
                if (option.value) {
                    if(controller.urls.length === 0){
                        // make sure field is ready
                        option._controller.ready.then(() => {

                            // only restore once
                            if (option.value) {

                                // multi upload support
                                let urls = option.value.split(',');

                                // NEW: editFile() does not support re-opening restored files (image editor shows error) - disable cropEdit until a new file is uploaded
                                if(option.params.cropEdit){
                                    option.params._cropEdit = option.params.cropEdit; // flag for re-enable
                                    delete option.params.cropEdit;
                                }

                                // UploadField.restore fetches file infos from server  INFO: a saved configuration restored its files from the archive before ( SavedConfigs._restoreFiles )
                                option._controller.field.restore(urls);

                                // value will be re-built via watchUploads
                                option.value = '';

                            }
                        });
                    }
                }
                // outside reset
                else if(controller.urls.length > 0){
                    if(option._controller.field){
                        // clear all files
                        option._controller.field.clearFiles();
                    }
                }
            },
            crop: (option, productForm) => {

                // NEW: check if we have a custom upload-lift crop config to update field
                if(option._controller._cropConfig) {
                    option._controller.updateCrop();
                }
                // NEW: upload-lift editor crop    IMPORTANT: skip if live crop is active (to avoid strange UX)
                else if(option.params.cropEdit && !(option._layer && option._layer.params.crop)){
                    // open crop editor (most recent file upload)
                    option._controller.field.editFile();
                }
                // default: LIVE crop on preview
                else {
                    productForm.dispatchEvent(new CustomEvent('option:crop', { detail: option, cancelable: true, bubbles: true }));
                }
            }
        }

        // NEW: adding ready promise on init - required for options restore to wait for the field to be initialized
        controller.ready = new Promise((resolve, reject) => {
            controller.onReady = resolve; // resolve ref to resolve after field is setup
        });

        const setupUploadLift = (e) => {

            const setupConfig = () => {

                let uploadApp = window.Cloudlift.upload.App;

                // INFO: we have to destroy the previously initialized field ( initialized by upload app auto setup with a found selector or no selector )
                // IMPROVEMENT: we could add a flag in the upload-lift.settings to disable setup ( only show flag if app installed & also reset if app is uninstalled )
                if(uploadApp.fields.length > 0){
                    for (let i = 0; i < uploadApp.fields.length; i++) {
                        const uploadField = uploadApp.fields[i];
                        if(uploadField.config.uuid == option.params.uuid){
                            uploadField.destroy();
                            uploadApp.fields.splice(i, 1);
                        }
                    }
                } else {
                    // NEW: disable setup (to avoid initializing later and duplicate upload fields)
                    uploadApp.config.setup = false;
                }

                for (let i = 0; i < window.Cloudlift.upload.config.fields.length; i++) {
                    const config = window.Cloudlift.upload.config.fields[i];

                    if(config.uuid === option.params.uuid){

                        // IMPORTANT: deep copy of the config to support same config on multiple options (mainly because we overwrite the field name)
                        const uploadConfig = JSON.parse(JSON.stringify(config));

                        // overwrite the field name with the option name (to make the options config source of truth)
                        uploadConfig.field = option.name;

                        // INFO: we use our own validate logic that does not call .onSubmit()  the upload-field - we disable fileFetch to make this not relevant ( if we trigger onSubmit we can remove this code )
                        uploadConfig.fileFetch = false;

                        // overwrite the selector to show inside the option ( supports multiple uses of the same field )
                        uploadConfig.selector = controller.selector;

                        // INFO: we manually initialize the field to ignore the conditions & to avoid calling app.setup() which does not work with multiple featured products on a single page
                        uploadApp._createUploadField(uploadConfig, product);

                        // get the new field from the array ( added by _createUploadField )
                        const fieldIndex = uploadApp.fields.length - 1;
                        controller.field = uploadApp.fields[fieldIndex];

                        // NEW: manually set the productForm reference to always make sure we sue the same instance (upload-lift in backend does not find the form as we do not render a real form tag)
                        controller.field._productForm = productForm;

                        // INFO: we remove the field from the app fields to exclude it from the upload-app._validateConfigs (has reference to uploadApp.fields)
                        // upload-app._validateFormSubmit is only called if a field was initialized on setup ( which can be the case or not )
                        // for this reason we trigger validation manually in controller.onValidate which also improves the validation UX
                        uploadApp.fields.splice(fieldIndex, 1);

                        // NEW: resolve ready promise only when filepond is ready
                        controller.field.element.addEventListener('upload:ready', () => {
                            option._controller.onReady(controller.field);
                        });

                        // NEW: dimension option crop ratio updates
                        this._migrateDimensionParams(option);
                        if(option.params.optionDimension && option.params.dimensionCrop){
                            controller.watchCrop(productForm, getOption);
                        }
                        // NEW: order size for upload exports from the dimension option (in this option's dimensionUnit)
                        if(option.params.optionDimension && option.params.dimensionUnit){
                            controller.watchExportSize(productForm, getOption);
                        }
                    }
                }

                // flag for editor mode
                if(!controller.field){
                    controller.notfound = true;
                }

            }

            // NEW: translation api lazy init can make .App not available
            // HACKY: setTimout delay to wait for the translations to be loaded and .App be ready - nicer would be to have a upload.ready promise
            let uploadAppReadyLazy = () => {
                setTimeout(() => {
                    if(window.Cloudlift.upload.App){
                        if(!controller.field){
                            setupConfig();
                        }
                    } else {
                        uploadAppReadyLazy(); // not yet ready
                    }
                }, 300);
            };

            // IMPORTANT: to check for the App class to be available
            if(window.Cloudlift && window.Cloudlift.upload){
                if(window.Cloudlift.upload.App){
                    setupConfig();
                } else {
                    uploadAppReadyLazy();
                }
            }

            // NEW: always listen for ready event - and setup if field not initialized (mainly required in backend editor when script is reloaded to get updated upload lift config)
            var uploadReady = (e) => {
                if(window.Cloudlift.upload.App){
                    if(!controller.field) { // NEW: only if field not already initialized
                        setupConfig();
                    }
                } else {
                    uploadAppReadyLazy();
                }
            };

            window.addEventListener('cloudlift.upload.ready', uploadReady, { once: true });

            controller.watchUploads();
        }

        // wait for options render to complete ( could be solved better by support custom javascript post render execution by option ( maybe define on _controller.postRender() )
        productForm.addEventListener('options:rendered', setupUploadLift, { once: true });

        return controller;
    }

    _pickFile(accept) {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = accept;
        input.hidden = true;
        return new Promise(resolve => {
            input.addEventListener('change', () => resolve(input.files[0] || null), { once: true });
            // no cancel event before Safari 16.4 > the promise stays pending (harmless - nothing is locked until a file is picked)
            input.addEventListener('cancel', () => resolve(null), { once: true });
            document.body.appendChild(input);
            input.click(); // stay in the user gesture for mobile browsers
        }).finally(() => input.remove());
    }

    _corsUrl(url){
        // CORS fix  TODO better cors url - or maybe CORS url is generated in the upload layer
        url = url.replace('https://upload.dev.cloudlift.app/s/dev', 'https://storage.googleapis.com/cloudlift-app-cloud-dev-upload');
        url = url.replace('https://upload.cloudlift.app/s', 'https://storage.googleapis.com/cloudlift-app-cloud-prod-upload');
        return url;
    }

    _migrateDimensionParams(option){
        let params = option.params;
        // old optionCrop setting auto migrate to new optionDimension + dimensionCrop
        if(params.optionCrop){
            params.optionDimension = params.optionCrop;
            params.dimensionCrop = true;
            delete params.optionCrop;
        }
    }

    async _dimensionUpdate(option, optionDimension, imgUrl){

        let params = option.params;

        // dimension option found && update enabled
        if(optionDimension && params.dimensionUpdate){

            // disable live crop to avoid multiple crop events (can auto open crop mode if ratio does not match)
            // INFO: async/await in getImgSize causes a render cycle on the preview
            let liveCrop = false;
            // only if a live preview layer is setup
            if(option._layer?.params.crop){
                option._layer.params.crop = false;
                liveCrop = true;
            }

            // fetch img size dimensions        CORS optional - but more efficient
            let imgSize = await Util.getImageSize(imgUrl);

            const ratio = Math.round((imgSize.width / imgSize.height) * 100) / 100;

            // optional set a fixed ratio based on the image
            if(params.dimensionRatio){
                // set fixed ratio on dimension
                optionDimension.params.ratio = ratio;
            }

            // set the crop ratio on upload (to avoid showing crop button while ratio matches)
            // only required for upload-lift (upload uses upload-renderer.js which evaluates the ratio)
            if(params.dimensionCrop && option.type === 'upload-lift' && typeof option._controller._cropRatio === 'undefined'){
                // ratio is inverse (y/x) on older image editor
                if(option._controller.field.config.imageEditorPintura){
                    option._controller._cropRatio = ratio;
                } else {
                    option._controller._cropRatio = Math.round((imgSize.height / imgSize.width) * 100) / 100;
                }
            }

            // convert to unit
            if(params.dimensionUnit){
                imgSize = Util.convertSizeToUnit(imgSize, params.dimensionDpi || 72, params.dimensionUnit);
            }

            // set inputs as done by a user (to trigger all the same logics)
            optionDimension._controller.xInput = String(imgSize.width);
            optionDimension._controller.yInput = String(imgSize.height);

            // re-enable live crop
            if(liveCrop){
                option._layer.params.crop = true;
                // if the crop box is updated - skip first crop box update (as we update the box to match the dimension input)
                if(params.dimensionCrop){
                    option._layer._autoCrop = false;
                }
            }

            // manual change event
            optionDimension._controller.handleChange(null, optionDimension, true);
        }
    }

    upload(fileMetaArray, onProgress){

        try {

            let request = this._provider.upload(fileMetaArray, onProgress);

            const uploadPromise = new Promise((resolve, reject) => {

                // register all the files on the meta api
                request.then((fileMetaArray) => {

                    for (let i = 0; i < fileMetaArray.length; i++) {
                        // free the native file object
                        delete fileMetaArray[i]._file;
                    }

                    // register all files - first file is registered as parent of the other files
                    let fileOriginal = fileMetaArray[0];

                    // NEW: content length check to avoid corrupt uploads (logic copied from Upload-Lift)
                    if(typeof fileOriginal.meta.contentLength !== 'undefined'){

                        // EXACT match
                        if(fileOriginal.size !== fileOriginal.meta.contentLength){

                            // logging for monitoring
                            ThemeConfig.previewExportLog && this._fileMeta.exportFailed({ upload: true, contentLength: fileOriginal.meta.contentLength, size: fileOriginal.size });

                            // force re-upload
                            reject({ upload: true, error: 'Upload failed - please try again'});
                            return;
                        }
                    }

                    this._fileMeta
                        .saveFile(fileOriginal)
                        .then((fileOriginal) => {
                            // save in store
                            // this._storeFile(fileOriginal);  NO STORE

                            // save all childs with parent set
                            const savePromises = [];

                            for (let i = 1; i < fileMetaArray.length; i++) {
                                const fileChild = fileMetaArray[i];

                                // set parent
                                fileChild.parent = fileOriginal.uuid;

                                savePromises.push(
                                    this._fileMeta.saveFile(fileChild).then((fileChild) => {
                                        // this._storeFile(fileOriginal);  NO STORE
                                        return fileChild;
                                    })
                                );
                            }

                            Promise.all(savePromises).then((files) => {
                                // add original at first position and resolve promise
                                files.unshift(fileOriginal);
                                // return files array
                                resolve(files);
                            });
                        })
                        .catch((err) => {
                            // continue error chain
                            reject(err);
                        });
                });

                request.catch((err) => {
                    // continue error chain
                    reject(err);
                })

            });

            // map cancel method
            uploadPromise.cancel = () => {
                request.cancel();
            };

            return uploadPromise;

        } catch(error){
            // error outside of promise - return a rejected Promise
            return Promise.reject(error);
        }
    }

}

export default Upload;
